<template>
  <v-dialog :fullscreen="$vuetify.breakpoint.smAndDown" :value="value" content-class="acq-dialog" max-width="860" persistent scrollable
            transition="dialog-transition" @keydown.esc="close">
    <v-card class="acq-dialog mdlg-scope tp-dialog">
      <div class="mdlg-header">
        <span :class="mode === 'assign' ? 'tone-warn' : 'tone-purple'" class="acq-avatar acq-avatar--sm">
          <v-icon small style="color: inherit">{{ mode === 'assign' ? 'mdi-domain' : 'mdi-bookmark-outline' }}</v-icon>
        </span>
        <div class="mdlg-header__text">
          <div class="acq-section-title">{{ mode === 'assign' ? 'Standorte zuteilen' : 'Standorte vormerken' }}</div>
          <div class="acq-tiny text-truncate">{{ personName || 'Lehrkraft' }}</div>
        </div>
        <v-btn :disabled="saving" aria-label="Schließen" icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </div>
      <v-card-text class="mdlg-body">
        <institution-picker v-if="canRead" v-model="selected" :active="value" :employee-id="employee._id" :initial-ids="initialIds" :known-names="knownNames"
                            :mode="mode" @teacher="loadedName = nameOf($event)"/>
        <div v-else class="acq-tiny py-6 text-center">Keine Berechtigung, die Standorte dieser Person zu sehen.</div>
      </v-card-text>
      <div class="mdlg-footer">
        <span class="mdlg-footer__state acq-tiny">
          <template v-if="dirty"><span class="mdlg-dot"/>{{ summary }}</template>
          <template v-else>Keine Änderungen</template>
        </span>
        <v-spacer/>
        <button :disabled="saving" class="cta cta--ghost" type="button" @click="close">Abbrechen</button>
        <button :disabled="!dirty || saving" class="cta cta--primary" type="button" @click="save">
          <v-progress-circular v-if="saving" indeterminate size="14" width="2"/>
          <v-icon v-else class="cta__icon" small>mdi-check</v-icon>Speichern
        </button>
      </div>
    </v-card>
    <discard-changes-dialog v-model="confirmDiscard" @confirm="discard"/>
  </v-dialog>
</template>

<script lang="ts">
import Vue from 'vue';
import {Types} from 'mongoose';
import InstitutionPicker from './institutionPicker.vue';
import DiscardChangesDialog from '@/components/common/modern/discardChangesDialog.vue';
import {EventBus} from '@/busses/EventBus';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {CrudActionTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudActionTypes';
import {EmployeeFields} from '@/classes/clientOnly/permissionTreeResources/enums/entityFields/EmployeeFields';
import {InstitutionFields} from '@/classes/clientOnly/permissionTreeResources/enums/entityFields/InstitutionFields';
import {PickerMode} from '@/components/institutions/institutionView/profileView/teacherPicker/teacherPicker.helper';
import {idsOf, namesOf, saveInstitutionAssignments} from './institutionPicker.helper';

export type PickerEmployee = {
  _id: Types.ObjectId;
  firstName?: string;
  lastName?: string;
  assignedInstitutions?: unknown[];
  plannedInstitutions?: unknown[];
};

/** Shared dialog of "Standorte zuteilen" and "Standorte vormerken": pick several schools for one person, then save all changes at once */
export default Vue.extend({
  components: {InstitutionPicker, DiscardChangesDialog},
  props: {
    value: {type: Boolean, required: true},
    mode: {type: String as () => PickerMode, required: true},
    employee: {type: Object as () => PickerEmployee, required: true},
    /** Optional institution id that is added to the selection when the dialog opens (e.g. "Vormerken" in the near view) */
    preselect: {type: String, default: ''},
    /** Display name of the preselected Standort (chip) */
    preselectName: {type: String, default: ''},
  },
  data: () => ({
    selected: [] as string[],
    saving: false,
    confirmDiscard: false,
    loadedName: '',
  }),
  computed: {
    field(): EmployeeFields {
      return this.mode === 'assign' ? EmployeeFields.ASSIGNED_INSTITUTIONS : EmployeeFields.PLANNED_INSTITUTIONS;
    },
    canRead(): boolean {
      return this.$$crudAccessManager.canAccessEntity(CrudEntityTypes.EMPLOYEE, CrudActionTypes.READ, this.employee._id, this.field)
        && this.$$crudAccessManager.canReadAllProvided(CrudEntityTypes.INSTITUTION, InstitutionFields.NAME);
    },
    list(): unknown[] {
      return (this.mode === 'assign' ? this.employee.assignedInstitutions : this.employee.plannedInstitutions) || [];
    },
    initialIds(): string[] {
      return idsOf(this.list);
    },
    knownNames(): Record<string, string> {
      const names = namesOf(this.list);
      return this.preselect && this.preselectName && !names[this.preselect] ? {...names, [this.preselect]: this.preselectName} : names;
    },
    personName(): string {
      return this.nameOf(this.employee) || this.loadedName;
    },
    added(): string[] {
      return this.selected.filter((id) => !this.initialIds.includes(id));
    },
    removed(): string[] {
      return this.initialIds.filter((id) => !this.selected.includes(id));
    },
    dirty(): boolean {
      return this.added.length > 0 || this.removed.length > 0;
    },
    summary(): string {
      return [this.added.length ? `${this.added.length} hinzugefügt` : '', this.removed.length ? `${this.removed.length} entfernt` : '']
        .filter(Boolean).join(' · ');
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(open: boolean) {
        if (open) {
          this.selected = this.preselect && !this.initialIds.includes(this.preselect) ? [...this.initialIds, this.preselect] : [...this.initialIds];
          this.confirmDiscard = false;
        }
      },
    },
  },
  methods: {
    nameOf(person: { firstName?: string; lastName?: string } | null): string {
      return `${person?.firstName || ''} ${person?.lastName || ''}`.trim();
    },
    close() {
      if (this.saving) {
        return;
      }
      if (this.dirty) {
        this.confirmDiscard = true;
        return;
      }
      this.$emit('input', false);
    },
    discard() {
      this.confirmDiscard = false;
      this.$emit('input', false);
    },
    async save() {
      this.saving = true;
      const assign = this.mode === 'assign';
      try {
        const failed = await saveInstitutionAssignments(this.mode, this.employee._id, {added: this.added, removed: this.removed});
        if (failed) {
          this.$$showSnackbar(`${failed} Änderung${failed === 1 ? '' : 'en'} konnte${failed === 1 ? '' : 'n'} nicht gespeichert werden. Bitte erneut versuchen.`,
            'error');
        } else {
          this.$$showSnackbar(assign ? 'Die Zuteilung wurde gespeichert.' : 'Die Vormerkung wurde gespeichert.', 'success');
        }
        this.$emit('update:employee');
        EventBus.$emit('update:employee');
        if (!failed) {
          this.$emit('input', false);
        }
      } finally {
        this.saving = false;
      }
    },
  },
});
</script>

<style>
.tp-dialog .mdlg-body {
  padding-top: 8px !important;
}
</style>
<style src="@/components/acquisitionList/ui/acquisitionViews.css"></style>
<style src="@/components/common/modern/modern.css"></style>
<style src="@/components/common/modern/modernDialog.css"></style>
